/* eslint-disable @typescript-eslint/no-explicit-any */
import React from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import {
    Box,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    IconButton
} from '@mui/material';
import Close from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';

import Modal from './modal/modal';
import Typography from './typography/typography';
import Button from './button/button';
import ModalLoader from './loaders/modal-loader';
import { DynamicInput, IModalComponentProps } from './form-control';

interface IFormValues {
    category_data: {
        category_name: string;
    };
}

const categoryValidationSchema = Yup.object().shape({
    category_data: Yup.object().shape({
        category_name: Yup.string().required('Category Name is required'),
    })
});

export const CategoryAddModal: React.FC<IModalComponentProps> = ({
    isOpen,
    setIsOpen,
    onSave,
    isLoading,
    modalProps,
}) => {
    const { t } = useTranslation();

    const { control, handleSubmit, reset, trigger } = useForm<any>({
        mode: 'all',
        resolver: yupResolver(categoryValidationSchema),
        defaultValues: {
            category_data: {
                category_name: '',
            }
        }
    });

    const handleModalClose = () => {
        reset({ category_data: { category_name: '' } });
        setIsOpen();
    };

    const onSubmit = async (values: IFormValues) => {
        const isValid = await trigger();
        if (!isValid) return;
        await onSave({
            category_data: {
                category_name: values.category_data.category_name,
                sku_prefix : values.category_data.sku_prefix,
                unique_items : values.category_data.unique_items,
                status: true,
            },
            attribute_data: [],
        } as any);
        handleModalClose();
    };

    return (
        <Modal
            open={isOpen}
            onClose={handleModalClose}
            maxWidth='sm'
            fullWidth
            {...modalProps}
        >
            <>
                <form onSubmit={handleSubmit(onSubmit)}>
                    {isLoading && <ModalLoader />}
                    <DialogTitle className='categoryAddModal--DialogTitle'>
                        <Typography type='s3' color='inherit' weight='medium'>
                            {t("inventory.item.categoryModal.title")}
                        </Typography>
                        <IconButton onClick={handleModalClose}>
                            <Close fontSize='small' />
                        </IconButton>
                    </DialogTitle>
                    <DialogContent className='categoryAddModal--DialogContents'>
                        <Grid container rowGap={1.5} columnSpacing={3} pt={3}>
                            <Grid item sm={12}>
                                <DynamicInput
                                    type='text'
                                    formControl={control}
                                    label={t("inventory.item.categoryModal.category_name_label")}
                                    name='category_name'
                                    fieldArrayName='category_data'
                                    placeholder={t("inventory.item.categoryModal.category_name_placeholder")}
                                    required
                                />
                            </Grid> 
                            <Grid item sm={12}>
                                <DynamicInput
                                    type='text'
                                    formControl={control}
                                    label={t("inventory.item.categoryModal.sku_prefix_label")}
                                    name='sku_prefix'
                                    fieldArrayName='category_data'
                                    placeholder={t("inventory.item.categoryModal.sku_prefix_placeholder")}
                                />
                            </Grid> 

                            <Grid item sm={12}>
                                <DynamicInput
                                    type='text'
                                    formControl={control}
                                    label={t("inventory.item.categoryModal.unique_items_label")}
                                    name='unique_items'
                                    fieldArrayName='category_data'
                                    placeholder={t("inventory.item.categoryModal.unique_items_placeholder")}
                                />
                            </Grid> 
                        </Grid>
                    </DialogContent>
                    <DialogActions className='categoryAddModal--DialogAction'>
                        <span></span>
                        <Box display='flex' gap={1.5}>
                            <Button
                                className='categoryAddModal--DialogAction--WhiteBtn'
                                onClick={handleModalClose}
                            >
                                {t("common.cancel")}
                            </Button>
                            <Button type='button' onClick={handleSubmit(onSubmit)}>{t("common.save")}</Button>
                        </Box>
                    </DialogActions>
                </form>
            </>
        </Modal>
    );
};
